import { z } from "zod";

import { readConsent } from "./cookie-consent";

/**
 * The creator whose link brought this visitor here, remembered until checkout.
 *
 * A discount code typed at checkout attributes revenue on its own — this exists
 * for the larger group who click a creator's link and never type the code at
 * all. Their sign-up would otherwise look organic, and the creator would be
 * judged on a fraction of what they drove.
 *
 * `localStorage`, not a cookie, for one reason: the API lives on a different
 * origin (`api.postdaily.app`), so a cookie set here would need an explicit
 * parent domain and `SameSite=None` to travel — configuration that fails
 * silently and in production. The value is read by our own client and passed
 * to `billing.startCheckout` as an input instead, where it is validated
 * against real codes. A forged value can therefore do exactly one thing: apply
 * a discount that already exists and would have worked if typed by hand.
 */
const STORAGE_KEY = "pd.creator-referral";

/**
 * How long a click stays attributable.
 *
 * Sixty days is what the creator program promises (see
 * `CREATOR_PROGRAM_TERMS.attributionWindowDays` in the API, mirrored here
 * because this module cannot import the server env). It sits in the middle of
 * the affiliate industry's 30-to-90-day range and comfortably covers the
 * trial-then-decide path this product sells on.
 */
const WINDOW_DAYS = 60;
const WINDOW_MS = WINDOW_DAYS * 24 * 60 * 60 * 1000;

/**
 * The stored shape, parsed rather than trusted.
 *
 * `localStorage` is an I/O boundary like any other: the value survives
 * deploys, so a future change to this record has to meet an old one still
 * sitting in someone's browser, and anything on the page can write to the key.
 */
const storedReferralSchema = z.object({
  code: z.string().min(1),
  storedAt: z.number(),
});

type StoredReferral = z.infer<typeof storedReferralSchema>;

/**
 * Every access is wrapped: Safari's private mode throws on `localStorage`
 * rather than returning null, and losing attribution must never be able to
 * break a page a visitor is trying to buy from.
 */
function readRaw(): string | null {
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

/** `JSON.parse` that yields `null` instead of throwing on malformed text. */
function safeJsonParse(raw: string) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * The code from a link the visitor followed before deciding about cookies.
 *
 * Held in the module, never on the device: an in-memory value for the life of
 * one page is the page's own runtime, not storage under PECR reg 6. It exists
 * so that someone who lands on `/r/CODE` and then accepts on the banner is
 * still attributed, instead of being told their click did not count. Navigate
 * away without deciding and it is simply gone, which is the correct outcome.
 *
 * It is deliberately NOT readable as attribution — see `readCreatorReferral`.
 * Until there is a yes, this creator did not earn the sale.
 */
let pendingCode: string | null = null;

export function storeCreatorReferral(code: string, now = Date.now()) {
  if (!readConsent().referral) {
    pendingCode = code;
    return;
  }

  pendingCode = null;
  writeReferral(code, now);
}

/**
 * Persist a code held from before the visitor accepted referral tracking.
 *
 * Called when consent is granted. Re-checks consent rather than trusting the
 * caller, because this writes to the device.
 */
export function commitPendingCreatorReferral(now = Date.now()) {
  if (!pendingCode || !readConsent().referral) {
    return;
  }

  const code = pendingCode;
  pendingCode = null;
  writeReferral(code, now);
}

/** Drop a held code — on rejection, or on withdrawal. */
export function forgetPendingCreatorReferral() {
  pendingCode = null;
}

function writeReferral(code: string, now: number) {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ code, storedAt: now } satisfies StoredReferral),
    );
  } catch {
    // No storage, no attribution — the code typed at checkout still works.
  }
}

/**
 * The remembered code, or null when absent, unreadable or expired.
 *
 * Reads only what was actually stored, and a value is only ever stored with
 * consent — so a held `pendingCode` is invisible here on purpose. Automatic
 * attribution without a yes is exactly what the consent gate exists to
 * prevent; a code typed at checkout is a separate, deliberate act and still
 * works.
 */
export function readCreatorReferral(now = Date.now()): string | null {
  const raw = readRaw();

  if (!raw) {
    return null;
  }

  const parsed = storedReferralSchema.safeParse(safeJsonParse(raw));

  if (!parsed.success) {
    return null;
  }

  // A clock moved backwards would otherwise make `now - storedAt` negative and
  // keep a stale referral alive indefinitely; anything not inside the window
  // going forwards is treated as expired.
  const age = now - parsed.data.storedAt;

  if (age < 0 || age > WINDOW_MS) {
    return null;
  }

  return parsed.data.code;
}

export function clearCreatorReferral() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to do — a stale entry expires on its own.
  }
}

/** Where a click lands when a code names no campaign path of its own. */
export const DEFAULT_REFERRAL_DESTINATION = "/pricing";

/**
 * A campaign landing path, or the default.
 *
 * `//evil.com` starts with a slash and is a protocol-relative URL, so a bare
 * "starts with /" check is the classic open-redirect hole. The admin schema
 * refuses to store one; this refuses to follow one. Two guards against the
 * same mistake, because only this one sits on the path a visitor walks.
 */
export function safeReferralDestination(
  landingPath: string | null | undefined,
) {
  if (!landingPath?.startsWith("/") || landingPath.startsWith("//")) {
    return DEFAULT_REFERRAL_DESTINATION;
  }

  return landingPath;
}
